import type { ReactNode } from "react";
import { useState, useEffect } from "react";
import { navigate } from "@reach/router";
import { useAuth } from "src/contexts/AuthContext";
import { useGameHistory } from "src/hooks/useGameHistory";
import { useToast } from "src/components/Toast";
import type { WeeklyStats } from "src/core/types";
import type { Theme } from "src/theme";

type Props = {
  path: string;
};

const containerStyle = ({ colors, gridUnit }: Theme) => ({
  position: "fixed" as const,
  left: 0,
  width: "100vw",
  height: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  background: colors.grey95,
  overflowY: "auto" as const,
  padding: gridUnit * 5
});

const cardStyle = ({ colors, gridUnit }: Theme) => ({
  background: "white",
  color: colors.textPrimary,
  padding: gridUnit * 10,
  borderRadius: gridUnit * 3,
  boxShadow: `0 ${gridUnit}px ${gridUnit * 1.5}px rgba(0,0,0,0.1)`,
  minWidth: "400px",
  maxWidth: "600px",
  width: "100%"
});

const sectionStyle = ({ colors, gridUnit }: Theme) => ({
  marginBottom: gridUnit * 8,
  padding: gridUnit * 6,
  background: colors.grey95,
  borderRadius: gridUnit * 2
});

const statCardBaseStyle = ({ gridUnit }: Theme) => ({
  padding: gridUnit * 4,
  borderRadius: gridUnit * 1.5,
  textAlign: "center" as const
});

const Profile = (props: Props): ReactNode => {
  const { user, signInWithGoogle, signOut, isAnonymous } = useAuth();
  const { getWeeklyStats, loading } = useGameHistory();
  const { showError } = useToast();
  const [stats, setStats] = useState<WeeklyStats | null>(null);
  const [signingIn, setSigningIn] = useState<boolean>(false);

  useEffect(() => {
    const loadStats = async () => {
      const weeklyStats = await getWeeklyStats();
      setStats(weeklyStats);
    };

    loadStats();
  }, [getWeeklyStats]);

  const handleGoogleSignIn = async () => {
    setSigningIn(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error("Sign in failed:", error);
      showError("Failed to sign in with Google. Please try again.");
    } finally {
      setSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Sign out failed:", error);
      showError("Failed to sign out. Please try again.");
    }
  };

  if (!user) {
    return (
      <div css={containerStyle}>
        <div css={(theme) => ({ color: theme.colors.textPrimary })}>Loading...</div>
      </div>
    );
  }

  return (
    <div css={containerStyle}>
      <div css={cardStyle}>
        <h1 css={(theme) => ({ marginTop: 0, marginBottom: theme.gridUnit * 7.5, textAlign: "center" as const })}>
          Profile & Stats
        </h1>

        {/* User Info Section */}
        <div css={sectionStyle}>
          <h3 css={(theme) => ({ marginTop: 0, marginBottom: theme.gridUnit * 4 })}>Account</h3>

          {isAnonymous ? (
            <div>
              <p css={(theme) => ({ margin: 0, marginBottom: theme.gridUnit * 4, color: theme.colors.grey1 })}>
                You're currently signed in as a guest. Sign in with Google to save your progress across devices.
              </p>
              <button
                css={(theme) => ({
                  ...theme.primaryButton,
                  width: "100%"
                })}
                onClick={handleGoogleSignIn}
                disabled={signingIn}
              >
                {signingIn ? "Signing in..." : "Sign in with Google"}
              </button>
            </div>
          ) : (
            <div>
              <div css={(theme) => ({ marginBottom: theme.gridUnit * 4 })}>
                <div
                  css={(theme) => ({
                    fontSize: theme.gridUnit * 3.5,
                    color: theme.colors.grey1,
                    marginBottom: theme.gridUnit
                  })}
                >
                  Email
                </div>
                <div css={{ fontWeight: "bold" }}>{user.email || "Not available"}</div>
              </div>
              <div css={(theme) => ({ marginBottom: theme.gridUnit * 4 })}>
                <div
                  css={(theme) => ({
                    fontSize: theme.gridUnit * 3.5,
                    color: theme.colors.grey1,
                    marginBottom: theme.gridUnit
                  })}
                >
                  Display Name
                </div>
                <div css={{ fontWeight: "bold" }}>{user.displayName || "Anonymous"}</div>
              </div>
              <button
                css={(theme) => ({
                  ...theme.secondaryButton,
                  width: "100%"
                })}
                onClick={handleSignOut}
              >
                Sign Out
              </button>
            </div>
          )}
        </div>

        {/* Stats Section */}
        <div css={(theme) => ({ marginBottom: theme.gridUnit * 8 })}>
          <h3 css={(theme) => ({ marginTop: 0, marginBottom: theme.gridUnit * 4 })}>Last 7 Days</h3>

          {loading ? (
            <div css={(theme) => ({ textAlign: "center" as const, padding: theme.gridUnit * 5 })}>Loading stats...</div>
          ) : stats ? (
            <div
              css={(theme) => ({
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: theme.gridUnit * 4
              })}
            >
              <div css={(theme) => ({ ...statCardBaseStyle(theme), background: "#e3f2fd" })}>
                <div
                  css={(theme) => ({ fontSize: theme.gridUnit * 8, fontWeight: "bold", color: theme.colors.primary })}
                >
                  {stats.gamesPlayed}
                </div>
                <div
                  css={(theme) => ({
                    fontSize: theme.gridUnit * 3.5,
                    color: theme.colors.grey1,
                    marginTop: theme.gridUnit
                  })}
                >
                  Games Played
                </div>
              </div>

              <div css={(theme) => ({ ...statCardBaseStyle(theme), background: "#e8f5e9" })}>
                <div css={(theme) => ({ fontSize: theme.gridUnit * 8, fontWeight: "bold", color: "#388e3c" })}>
                  {stats.gamesWon}
                </div>
                <div
                  css={(theme) => ({
                    fontSize: theme.gridUnit * 3.5,
                    color: theme.colors.grey1,
                    marginTop: theme.gridUnit
                  })}
                >
                  Games Won
                </div>
              </div>

              <div css={(theme) => ({ ...statCardBaseStyle(theme), background: "#ffebee" })}>
                <div css={(theme) => ({ fontSize: theme.gridUnit * 8, fontWeight: "bold", color: "#d32f2f" })}>
                  {stats.gamesLost}
                </div>
                <div
                  css={(theme) => ({
                    fontSize: theme.gridUnit * 3.5,
                    color: theme.colors.grey1,
                    marginTop: theme.gridUnit
                  })}
                >
                  Games Lost
                </div>
              </div>

              <div css={(theme) => ({ ...statCardBaseStyle(theme), background: "#f3e5f5" })}>
                <div css={(theme) => ({ fontSize: theme.gridUnit * 8, fontWeight: "bold", color: "#7b1fa2" })}>
                  {Math.round(stats.winRate * 100)}%
                </div>
                <div
                  css={(theme) => ({
                    fontSize: theme.gridUnit * 3.5,
                    color: theme.colors.grey1,
                    marginTop: theme.gridUnit
                  })}
                >
                  Win Rate
                </div>
              </div>

              <div
                css={(theme) => ({
                  ...statCardBaseStyle(theme),
                  background: "#fff3e0",
                  gridColumn: "1 / -1"
                })}
              >
                <div css={(theme) => ({ fontSize: theme.gridUnit * 7, fontWeight: "bold", color: "#e65100" })}>
                  {stats.averageTurns}
                </div>
                <div
                  css={(theme) => ({
                    fontSize: theme.gridUnit * 3.5,
                    color: theme.colors.grey1,
                    marginTop: theme.gridUnit
                  })}
                >
                  Average Turns per Game
                </div>
              </div>
            </div>
          ) : (
            <div
              css={(theme) => ({
                textAlign: "center" as const,
                padding: theme.gridUnit * 5,
                color: theme.colors.grey1
              })}
            >
              No game history available
            </div>
          )}
        </div>

        {/* Back Button */}
        <button
          css={(theme) => ({
            ...theme.button,
            width: "100%"
          })}
          onClick={() => navigate("/")}
        >
          Back to Home
        </button>
      </div>
    </div>
  );
};

export default Profile;
